import apiService from './apiService';
import { getActiveTimezone } from '../utils/timezone';

export const reminderService = {
    async listReminders() {
        // Send the LIVE browser timezone so the BE can lazily generate the 24h
        // "set the time" alerts for untimed (TBC) events in the user's REAL tz —
        // the hourly cron keys off the stale stored User.timezone and misses
        // non-UTC users (24hour prior Notification: "simply not working").
        let qs = '';
        try {
            const tz = getActiveTimezone(); // user's reference tz, else browser (#125)
            if (tz) qs = `?tz=${encodeURIComponent(tz)}`;
        } catch (_) { /* BE falls back to the stored user tz */ }
        return apiService.get(`/api/reminders${qs}`);
    },

    // Notes & Reminders — the PLAIN management list for the Reminders panel
    // column: ONLY kind='reminder'. No calendar-event merge, no lazy TBC-alert
    // generation (that stays on the Bell GET above).
    //
    // Past Reminders (#284) splits it by lifecycle state:
    //   view 'active' (default) → upcoming only, soonest first
    //   view 'past'             → already fired, most recent first, auto-removed
    //                             by the backend 7 days after firing
    // → { items: [{id,title,remind_at,pushed_at,created_at,is_past}], count, view }
    async listManage({ view = 'active' } = {}) {
        const qs = view === 'past' ? '?manage=true&view=past' : '?manage=true';
        return apiService.get(`/api/reminders${qs}`);
    },

    // Recurring reminders: `recurrence` is 'daily' | 'weekly' | 'every_n_days' |
    // 'monthly' — or 'none'/omitted for a one-off (the default). A weekly rule
    // carries `recurrence_days` (Mon=0 … Sun=6); 'every_n_days' carries
    // `recurrence_interval_days`. The BE validates and, for a weekly rule, snaps
    // the first occurrence onto the chosen weekdays.
    // Notification Bug (#346): `kind` defaults to the genuine, user-requested
    // 'reminder' — pass `kind: 'system'` ONLY for an automated notice PAI
    // generated on its own (e.g. backgroundDelete.js's completion notice), so
    // it renders as a "System Notification" in the Bell instead of a Reminder
    // and never leaks into the Notes & Reminders module / PAI's tools.
    //
    // Notification Center misalignment (#379): a self-directed system notice
    // may ALSO declare `notif_type` (a "feature.event" from the Notification
    // Center taxonomy, allow-listed server-side). That routes it through the
    // preference gate instead of writing a raw Bell row — so the user's own
    // toggles decide whether it appears and whether it reaches
    // Telegram. Without it a toggle like Vault → "Processing Completed" is
    // inert: the notice is minted here (only the browser knows a folder or
    // archive BATCH finished) and never met the matrix. The response is a 202
    // envelope, not a reminder row.
    // Expiration Reminders (#463): a repeating reminder may end by COUNT
    // (`recurrence_count` = the TOTAL number of times) and/or by DATE
    // (`recurrence_until` = the last local day, 'YYYY-MM-DD'). Only valid with a
    // recurrence; null = never ends.
    async createReminder({
        title, remind_at, link = null, kind = 'reminder', notif_type = null,
        recurrence = null, recurrence_days = null, recurrence_interval_days = null,
        recurrence_count = null, recurrence_until = null,
    }) {
        return apiService.post('/api/reminders', {
            title, remind_at, link, kind, notif_type,
            recurrence, recurrence_days, recurrence_interval_days,
            recurrence_count, recurrence_until,
            // The live browser tz: the BE reads the rule (weekday snap, the end
            // date's local calendar) in it, not in a possibly stale stored tz.
            timezone: getActiveTimezone(),
        });
    },

    // Edit a plain reminder's title and/or time (changing remind_at re-arms the
    // proactive push server-side).
    async updateReminder(reminderId, patch) {
        return apiService.patch(`/api/reminders/${reminderId}`, { timezone: getActiveTimezone(), ...patch });
    },

    async clearReminder(reminderId) {
        return apiService.patch(`/api/reminders/${reminderId}/clear`, {});
    },

    async deleteReminder(reminderId) {
        return apiService.delete(`/api/reminders/${reminderId}`);
    },

    // Attached documents to reminders (#386). Linking needs a document the
    // user OWNS (the BE refuses a shared one with a 400 that says why);
    // removing only drops the link — the file stays in the File Manager.
    // Both return { attachments: [...] }, the reminder's full list afterwards.
    async addAttachment(reminderId, documentId) {
        return apiService.post(`/api/reminders/${reminderId}/attachments`, { document_id: documentId });
    },

    async removeAttachment(reminderId, documentId) {
        return apiService.delete(`/api/reminders/${reminderId}/attachments/${documentId}`);
    },

    // Kind-safe bulk delete (only kind='reminder' rows can ever be removed).
    async bulkDelete(reminderIds) {
        return apiService.post('/api/reminders/bulk-delete', { reminder_ids: reminderIds });
    },
};

export default reminderService;
